import React, { useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
  Alert,
} from "@mui/material";

const ITEM_HEIGHT = 44;
const ITEM_PADDING_TOP = 8;

const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 8 + ITEM_PADDING_TOP,
    },
  },
};

const truncateText = (text, maxLength = 35) => {
  if (!text) {
    return "";
  }

  if (text.length <= maxLength) {
    return text;
  }

  return `${text.substring(0, maxLength)}...`;
};

export default function ProductCatalogueMultiSelect({
  headerText = "Produktkatalog",
  tooltipText,
  items = [],
  text,
  setText,
  jsonText = "productCatalogueItems",
  validateIsErr = {},
  errText = "",
}) {
  const selectedItems = Array.isArray(text?.[jsonText]) ? text[jsonText] : [];
  const hasError = validateIsErr?.[jsonText] === true;

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => a.SortOrder - b.SortOrder);
  }, [items]);

  const itemMapById = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      map.set(item.Id, item);
    });
    return map;
  }, [items]);

  const selectedIds = selectedItems.map((x) => x.id);

  const hasSelectedItemWithSla = selectedItems.some(
    (item) => item?.slaClass !== null && item?.slaClass !== undefined,
  );

  const showMissingSlaWarning =
    selectedItems.length > 0 && !hasSelectedItemWithSla;

  const handleSelectChange = (event) => {
    const value = event.target.value;
    const nextIds =
      typeof value === "string" ? value.split(",").map(Number) : value;

    const currentById = new Map(selectedItems.map((x) => [x.id, x]));

    const nextSelectedItems = nextIds
      .map((id) => {
        const original = itemMapById.get(id);

        if (!original || original.IsGroupHeader || !original.ItemCode) {
          return null;
        }

        const existing = currentById.get(id);

        return {
          id: original.Id,
          code: original.ItemCode,
          groupCode: original.GroupCode,
          stdJob: original.StdJob,
          hasDiscount: original.HasDiscount,
          slaClass: original.SlaClass ?? null,
          amount: existing?.amount ?? 1,
        };
      })
      .filter(Boolean);

    setText((prev) => ({
      ...prev,
      [jsonText]: nextSelectedItems,
    }));
  };

  const handleAmountChange = (id, value) => {
    const parsedValue = Number(value);

    const nextSelectedItems = selectedItems.map((item) =>
      item.id === id
        ? {
            ...item,
            amount: Number.isNaN(parsedValue) ? 1 : parsedValue,
          }
        : item,
    );

    setText((prev) => ({
      ...prev,
      [jsonText]: nextSelectedItems,
    }));
  };

  const handleRemove = (id) => {
    const nextSelectedItems = selectedItems.filter((item) => item.id !== id);

    setText((prev) => ({
      ...prev,
      [jsonText]: nextSelectedItems,
    }));
  };

  return (
    <Box sx={{ width: "100%", marginLeft: "6px" }}>
      <Typography
        component="h6"
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          mb: 0.75,
          color: "#1f2937",
        }}
      >
        {headerText}
      </Typography>

      <FormControl fullWidth size="small">
        <Select
          multiple
          displayEmpty
          value={selectedIds}
          onChange={handleSelectChange}
          MenuProps={MenuProps}
          renderValue={(selected) => {
            if (!selected.length) {
              return (
                <Typography
                  sx={{
                    color: "#4b5563",
                    fontSize: "16px",
                  }}
                >
                  Välj tillägg...
                </Typography>
              );
            }

            return (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  py: 0.25,
                }}
              >
                {selected.map((id) => {
                  const item = itemMapById.get(id);
                  const chipLabel = item
                    ? `${item.GroupCode}-${item.ItemCode}`
                    : `${id}`;

                  const fullLabel = item
                    ? `${item.GroupCode} - ${item.ItemCode} - ${item.ItemDescription}`
                    : `${id}`;

                  return (
                    <Chip
                      key={id}
                      label={chipLabel}
                      size="small"
                      title={fullLabel}
                      sx={{
                        height: 24,
                        backgroundColor: "#f3f4f6",
                        borderRadius: "12px",
                        "& .MuiChip-label": {
                          px: 1,
                          fontSize: "13px",
                        },
                      }}
                    />
                  );
                })}
              </Box>
            );
          }}
          sx={{
            minHeight: "38px",
            backgroundColor: "#d8d9db",
            borderRadius: "10px",
            fontSize: "16px",
            boxShadow: "none",
            ".MuiOutlinedInput-notchedOutline": {
              border: hasError ? "1px solid #d32f2f" : "none",
            },
            "&:hover .MuiOutlinedInput-notchedOutline": {
              border: hasError ? "1px solid #d32f2f" : "none",
            },
            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              border: hasError ? "1px solid #d32f2f" : "1px solid #bfc3c8",
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              minHeight: "38px",
              padding: "4px 38px 4px 12px !important",
            },
            "& .MuiSelect-icon": {
              color: "#9ca3af",
            },
          }}
        >
          {sortedItems.map((item) => {
            if (item.IsGroupHeader) {
              const fullHeaderText = `${item.GroupCode} - ${item.GroupDescription}`;

              return (
                <MenuItem
                  key={item.Id}
                  disabled
                  sx={{
                    opacity: 1,
                    backgroundColor: "#e8f4fd",
                    color: "#0b5cab",
                    fontWeight: 700,
                    "&.Mui-disabled": {
                      opacity: 1,
                      color: "#0b5cab",
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography
                        component="span"
                        title={fullHeaderText}
                        sx={{
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                          cursor: "help",
                          fontWeight: 700,
                        }}
                      >
                        {truncateText(fullHeaderText, 60)}
                      </Typography>
                    }
                  />
                </MenuItem>
              );
            }

            const fullItemText = `${item.GroupCode} - ${item.ItemCode} - ${item.ItemDescription}`;
            const hasSla = item.SlaClass !== null && item.SlaClass !== undefined;

            return (
              <MenuItem key={item.Id} value={item.Id}>
                <Checkbox checked={selectedIds.includes(item.Id)} />
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        minWidth: 0,
                      }}
                    >
                      <Typography
                        component="span"
                        title={fullItemText}
                        sx={{
                          display: "block",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: "100%",
                          cursor: "help",
                          flex: 1,
                        }}
                      >
                        {truncateText(fullItemText, 60)}
                      </Typography>

                      {hasSla && (
                        <Chip
                          label="SLA"
                          size="small"
                          sx={{
                            height: 22,
                            backgroundColor: "#e8f5e9",
                            color: "#2e7d32",
                            fontSize: "11px",
                            fontWeight: 600,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={`Enhet: ${item.Unit ?? "-"}`}
                />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      {tooltipText && (
        <Typography
          sx={{
            mt: 1,
            fontSize: "14px",
            color: "#4b5563",
            lineHeight: 1.45,
          }}
        >
          {tooltipText}
        </Typography>
      )}

      {showMissingSlaWarning && (
        <Alert severity="warning" sx={{ mt: 1 }}>
          Du har valt tillägg som saknar SLA. Välj minst en produkt med SLA innan
          du går vidare.
        </Alert>
      )}

      <small
        style={{
          color: "#d32f2f",
          display: "block",
          minHeight: "18px",
          marginTop: "4px",
        }}
      >
        {hasError ? errText : ""}
      </small>

      {selectedItems.length > 0 && (
        <Box
          sx={{
            mt: 1,
            border: "1px solid #d6d6d6",
            borderRadius: "4px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          {selectedItems.map((selected, index) => {
            const sourceItem = itemMapById.get(selected.id);
            const fullText = sourceItem
              ? `${sourceItem.GroupCode} - ${sourceItem.ItemCode} - ${sourceItem.ItemDescription}`
              : "";

            const hasSla =
              selected?.slaClass !== null && selected?.slaClass !== undefined;

            return (
              <Box
                key={selected.id}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    md: "1fr auto 80px 90px",
                  },
                  gap: 1,
                  alignItems: "center",
                  px: 1.25,
                  py: 0.75,
                  minHeight: 46,
                  borderBottom:
                    index === selectedItems.length - 1
                      ? "none"
                      : "1px solid #e5e7eb",
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Tooltip title={fullText} placement="top" arrow>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 500,
                        color: "#111827",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: "100%",
                        cursor: "help",
                      }}
                    >
                      {truncateText(fullText, 35)}
                    </Typography>
                  </Tooltip>
                </Box>

                <Box>
                  {hasSla && (
                    <Chip
                      label="SLA"
                      size="small"
                      sx={{
                        height: 22,
                        backgroundColor: "#e8f5e9",
                        color: "#2e7d32",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    />
                  )}
                </Box>

                <TextField
                  size="small"
                  variant="outlined"
                  type="number"
                  value={selected.amount ?? 1}
                  onChange={(e) =>
                    handleAmountChange(selected.id, e.target.value)
                  }
                  inputProps={{
                    min: 0,
                    step: "any",
                  }}
                  sx={{
                    width: "100%",
                    maxWidth: 80,
                    "& .MuiOutlinedInput-root": {
                      height: 32,
                      backgroundColor: "#fff",
                    },
                    "& .MuiInputBase-input": {
                      padding: "6px 8px",
                      fontSize: "13px",
                    },
                  }}
                />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: { xs: "flex-start", md: "flex-end" },
                  }}
                >
                  <button
                    type="button"
                    onClick={() => handleRemove(selected.id)}
                    style={{
                      border: "1px solid #ef4444",
                      background: "transparent",
                      color: "#ef4444",
                      borderRadius: "999px",
                      padding: "5px 12px",
                      cursor: "pointer",
                      fontSize: "13px",
                      lineHeight: 1.2,
                    }}
                  >
                    Ta bort
                  </button>
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}